import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Navigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, Download, Eye, EyeOff, Loader2, Upload } from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { isFreePlan } from "@/components/shared/PaidPlanRoute";
import { PageHeader } from "@/components/shared/PageHeader";
import { LoadingPage } from "@/components/shared/LoadingPage";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { CertificateUploadForm } from "@/components/shared/CertificateUploadForm";
import { CompanyImageCard } from "@/components/features/company-image/CompanyImageCard";
import { BrandsCard } from "@/components/features/brands/BrandsCard";
import { CompanyInvoiceIdentitySection } from "@/components/features/company-image/CompanyInvoiceIdentitySection";
import { CompanyAdendaCard } from "@/components/features/adenda/CompanyAdendaCard";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PlanGateOverlay } from "@/components/shared/PlanGateOverlay";
import { isProOrUltraPlan } from "@/components/shared/PaidPlanRoute";
import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { ImportEmittedCfeDialog } from "@/pages/tenants/ImportEmittedCfeDialog";
import { ImportRecibidasCfeDialog } from "@/pages/tenants/ImportRecibidasCfeDialog";
import { SettingsCaeCard } from "@/pages/settings/SettingsCaeCard";
import {
  importEmittedCfes,
  importRecibidasCfes,
  downloadExportEmittedCfes,
  downloadExportRecibidasCfes,
} from "@/services/invoiceService";
import { LiteralEInfoModal } from "@/components/shared/LiteralEInfoModal";
import { SettingsApiIntegrationCard } from "@/pages/settings/SettingsApiIntegrationCard";
import { SettingsDeelIntegrationCard } from "@/pages/settings/SettingsDeelIntegrationCard";
import { SettingsMercadopagoIntegrationCard } from "@/pages/settings/SettingsMercadopagoIntegrationCard";
import { SettingsFiscalProfileCard } from "@/pages/settings/SettingsFiscalProfileCard";
import { SettingsFiscalAutomationCard } from "@/pages/settings/SettingsFiscalAutomationCard";
import { SettingsAutoAcceptReceivedCfeCard } from "@/pages/settings/SettingsAutoAcceptReceivedCfeCard";
import { SettingsAutoAcceptRucsCard } from "@/pages/settings/SettingsAutoAcceptRucsCard";
import { SettingsLegalInfoCard } from "@/pages/settings/SettingsLegalInfoCard";
import { SettingsFiscalIdentityCard } from "@/pages/settings/SettingsFiscalIdentityCard";
import {
  getFiscalIdentity,
  getFiscalIdentityFromDgi,
  updateFiscalIdentity,
} from "@/services/fiscalIdentityService";
import { SettingsPlanCard } from "@/pages/settings/SettingsPlanCard";
import { SubscriptionInvoicesTable } from "@/pages/settings/SubscriptionInvoicesTable";
import {
  getTenantCurrent,
  getCurrentTenantDgiCredentialsStatus,
  downloadCurrentTenantDgiCertificate,
  revealCurrentTenantDgiPassphrase,
  uploadCurrentTenantImage,
  requestTenantDeletion,
  getTenantPlans,
  getCurrentPartnerBenefit,
  updateTenant,
  updateTenantPlan,
  updateTenantBranding,
  updateTenantAdenda,
  useSiteLogoForInvoice as apiUseSiteLogoForInvoice,
  type InvoiceLogoCropPayload,
  type TenantCurrentResponse,
  type CurrentTenantDgiCredentialsStatus,
  type TenantPlanOption,
} from "@/services/authService";
import {
  createBankTransferCheckout,
  createBankTransferIntent,
  createPlanCheckout,
} from "@/services/planPaymentsService";
import {
  BankTransferPanel,
  grossWithIva,
} from "@/components/features/accountant/BankTransferPanel";
import { tenantPlanPrice } from "@/lib/tenant-plan-pricing";
import { formatDisplayDate, parseLocalDateOnly } from "@/lib/dateFormat";
import { tenantKeys } from "@/lib/queryKeys";
import { apiClient } from "@/services/apiClient";
import { serverMessage } from "@/lib/serverMessage";
import { planUsesActivationPath } from "@/pages/onboarding/planActivation";

const TAB_COMPANY = "company";
const TAB_FISCAL_LEGAL = "fiscal-legal";
const TAB_CERTIFICATES = "certificates";
const TAB_BILLING = "billing";
const TAB_API = "api";
const TAB_INTEGRACIONES = "integraciones";
const SETTINGS_TABS = [
  TAB_COMPANY,
  TAB_FISCAL_LEGAL,
  TAB_CERTIFICATES,
  TAB_BILLING,
  TAB_API,
  TAB_INTEGRACIONES,
] as const;
const CERT_WARNING_DAYS = 60;
const CERT_DANGER_DAYS = 30;

function getCertificateValidityDisplay(
  validFrom: string,
  validTo: string
): { progress: number; indicatorClassName: string } {
  const from = parseLocalDateOnly(validFrom);
  const to = parseLocalDateOnly(validTo);
  const today = new Date();
  from.setHours(0, 0, 0, 0);
  to.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  const totalMs = to.getTime() - from.getTime();
  const elapsedMs = today.getTime() - from.getTime();
  const progress =
    totalMs <= 0 ? 0 : Math.min(100, Math.max(0, (elapsedMs / totalMs) * 100));
  const daysUntilExpiry = Math.ceil(
    (to.getTime() - today.getTime()) / (24 * 60 * 60 * 1000)
  );

  let indicatorClassName = "bg-green-500 dark:bg-green-600";
  if (daysUntilExpiry < 0) {
    indicatorClassName = "bg-destructive";
  } else if (daysUntilExpiry <= CERT_DANGER_DAYS) {
    indicatorClassName = "bg-red-500 dark:bg-red-600";
  } else if (daysUntilExpiry <= CERT_WARNING_DAYS) {
    indicatorClassName = "bg-yellow-500 dark:bg-yellow-600";
  }
  return { progress, indicatorClassName };
}

function CertificateValidityBar({
  validFrom,
  validTo,
  ariaLabel,
}: {
  validFrom: string;
  validTo: string;
  ariaLabel: string;
}): JSX.Element {
  const { progress, indicatorClassName } = getCertificateValidityDisplay(
    validFrom,
    validTo
  );
  return (
    <div className="space-y-2">
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{formatDisplayDate(validFrom)}</span>
        <span>{formatDisplayDate(validTo)}</span>
      </div>
      <Progress
        value={progress}
        max={100}
        indicatorClassName={indicatorClassName}
        className="h-2"
        aria-label={ariaLabel}
      />
    </div>
  );
}

export function SettingsPage(): JSX.Element {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get("tab");
  const isAccountant = user?.role === "accountant";

  const { data: tenant, isLoading: tenantLoading } = useQuery({
    queryKey: tenantKeys.current(user?.tenantId ?? undefined),
    queryFn: getTenantCurrent,
    enabled: Boolean(user?.tenantId),
  });

  const tenantIsFree =
    tenant != null && isFreePlan(tenant.planId);

  const tabFromUrl = SETTINGS_TABS.includes(tabParam as (typeof SETTINGS_TABS)[number])
    ? tabParam
    : TAB_COMPANY;
  const tabForOwnerPlan =
    tenantIsFree &&
    (tabFromUrl === TAB_API ||
      tabFromUrl === TAB_INTEGRACIONES ||
      tabFromUrl === TAB_FISCAL_LEGAL)
      ? TAB_COMPANY
      : tabFromUrl;
  const effectiveTab =
    isAccountant && (tabForOwnerPlan === TAB_BILLING || tabForOwnerPlan === TAB_API)
      ? tenantIsFree
        ? TAB_COMPANY
        : TAB_FISCAL_LEGAL
      : tabForOwnerPlan;

  useEffect(() => {
    if (!isAccountant) return;
    if (tabParam !== TAB_BILLING && tabParam !== TAB_API) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", TAB_FISCAL_LEGAL);
        return next;
      },
      { replace: true }
    );
  }, [isAccountant, tabParam, setSearchParams]);

  useEffect(() => {
    if (!tenantIsFree) return;
    if (tabParam !== TAB_API && tabParam !== TAB_FISCAL_LEGAL) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("tab");
        return next;
      },
      { replace: true }
    );
  }, [tenantIsFree, tabParam, setSearchParams]);

  const setTab = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === TAB_COMPANY) next.delete("tab");
          else next.set("tab", value);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const { data: plans = [] } = useQuery({
    queryKey: ["tenants", "current", "plans"] as const,
    queryFn: getTenantPlans,
    enabled: Boolean(user?.tenantId),
  });

  const { data: partnerBenefit = null } = useQuery({
    queryKey: ["tenants", "current", "partner-benefit"] as const,
    queryFn: getCurrentPartnerBenefit,
    enabled: Boolean(user?.tenantId),
  });

  const [certStatus, setCertStatus] = useState<CurrentTenantDgiCredentialsStatus | null>(null);
  const [certLoading, setCertLoading] = useState(false);
  const [showCertForm, setShowCertForm] = useState(false);
  const [certDownloading, setCertDownloading] = useState(false);
  const [revealedPassphrase, setRevealedPassphrase] = useState<string | null>(null);
  const [passphraseRevealing, setPassphraseRevealing] = useState(false);

  const [companyImageUploading, setCompanyImageUploading] = useState(false);
  const [siteLogoUploading, setSiteLogoUploading] = useState(false);
  const [invoiceLogoUploading, setInvoiceLogoUploading] = useState(false);
  const [useSameLogo, setUseSameLogo] = useState(true);
  const [deletionDialogOpen, setDeletionDialogOpen] = useState(false);
  const [deletionLoading, setDeletionLoading] = useState(false);
  const [importEmittedOpen, setImportEmittedOpen] = useState(false);
  const [importRecibidasOpen, setImportRecibidasOpen] = useState(false);
  const [exportEmittedLoading, setExportEmittedLoading] = useState(false);
  const [exportRecibidasLoading, setExportRecibidasLoading] = useState(false);
  const [previewPdfLoading, setPreviewPdfLoading] = useState(false);
  const [brandColorSaving, setBrandColorSaving] = useState(false);
  const [adendaSaving, setAdendaSaving] = useState(false);
  const [invoiceLogoCropSaving, setInvoiceLogoCropSaving] = useState(false);
  const [planSaving, setPlanSaving] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [selectedBillingInterval, setSelectedBillingInterval] = useState<"monthly" | "annual">("monthly");
  const [literalEInfoModalOpen, setLiteralEInfoModalOpen] = useState(false);
  const [publicNameSaving, setPublicNameSaving] = useState(false);
  const [publicNameValue, setPublicNameValue] = useState("");
  const planPaymentReturnHandledRef = useRef(false);
  useEffect(() => {
    if (tenant != null) setPublicNameValue(tenant.name ?? "");
  }, [tenant?.id, tenant?.name]);

  useEffect(() => {
    if (tenantLoading || !tenant) return;
    const flag = searchParams.get("plan_payment");
    if (!flag) {
      planPaymentReturnHandledRef.current = false;
      return;
    }
    if (planPaymentReturnHandledRef.current) return;
    planPaymentReturnHandledRef.current = true;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("plan_payment");
        next.delete("checkout_id");
        return next;
      },
      { replace: true }
    );
    if (flag === "done") {
      void queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
      toast.success(t("settings.plans.updateSuccess"));
    } else if (flag === "failed") {
      toast.error(t("pay.planPaymentFailed"));
    } else if (flag === "timeout") {
      toast.error(t("pay.planPaymentTimeout"));
    }
  }, [tenantLoading, tenant, searchParams, setSearchParams, queryClient, t]);

  const loadCertStatus = useCallback(async (): Promise<void> => {
    if (!user?.tenantId) return;
    setCertLoading(true);
    try {
      const status = await getCurrentTenantDgiCredentialsStatus();
      setCertStatus(status);
    } catch {
      setCertStatus(null);
    } finally {
      setCertLoading(false);
    }
  }, [user?.tenantId]);

  useEffect(() => {
    void loadCertStatus();
  }, [loadCertStatus]);

  const handleCertDownload = useCallback(async (): Promise<void> => {
    setCertDownloading(true);
    try {
      const { blob, filename } = await downloadCurrentTenantDgiCertificate();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t("settings.certificate.errorDownload", { lng: "es" }));
    } finally {
      setCertDownloading(false);
    }
  }, [t]);

  const handleTogglePassphrase = useCallback(async (): Promise<void> => {
    if (revealedPassphrase !== null) {
      setRevealedPassphrase(null);
      return;
    }
    setPassphraseRevealing(true);
    try {
      const { passphrase } = await revealCurrentTenantDgiPassphrase();
      setRevealedPassphrase(passphrase ?? "");
    } catch {
      toast.error(t("settings.certificate.errorReveal", { lng: "es" }));
    } finally {
      setPassphraseRevealing(false);
    }
  }, [revealedPassphrase, t]);

  useEffect(() => {
    if (tenant?.planId) setSelectedPlanId(tenant.planId);
    if (tenant?.billingInterval === "monthly" || tenant?.billingInterval === "annual") {
      setSelectedBillingInterval(tenant.billingInterval);
    }
  }, [tenant?.planId, tenant?.billingInterval]);

  useEffect(() => {
    if (tenant == null) return;
    const same =
      tenant.invoiceLogoImage == null || tenant.invoiceLogoImage === tenant.profileImage;
    setUseSameLogo(same);
  }, [tenant?.profileImage, tenant?.invoiceLogoImage]);

  const JPG_PNG_MIME = ["image/jpeg", "image/png"] as const;

  const handleBothLogoChange = useCallback(
    async (file: File | null): Promise<void> => {
      if (!file) return;
      setCompanyImageUploading(true);
      try {
        await uploadCurrentTenantImage(file, "both");
        await queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
        await refreshUser();
        toast.success(t("settings.companyImage.success"));
      } catch {
        toast.error(t("settings.companyImage.error"));
      } finally {
        setCompanyImageUploading(false);
      }
    },
    [queryClient, refreshUser, t]
  );

  const handleSiteLogoChange = useCallback(
    async (file: File | null): Promise<void> => {
      if (!file) return;
      setSiteLogoUploading(true);
      try {
        await uploadCurrentTenantImage(file, "site");
        await queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
        await refreshUser();
        toast.success(t("settings.companyImage.success"));
      } catch {
        toast.error(t("settings.companyImage.error"));
      } finally {
        setSiteLogoUploading(false);
      }
    },
    [queryClient, refreshUser, t]
  );

  const handleInvoiceLogoChange = useCallback(
    async (file: File | null): Promise<void> => {
      if (!file) return;
      setInvoiceLogoUploading(true);
      try {
        await uploadCurrentTenantImage(file, "invoice");
        await queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
        await refreshUser();
        toast.success(t("settings.companyImage.success"));
      } catch {
        toast.error(t("settings.companyImage.error"));
      } finally {
        setInvoiceLogoUploading(false);
      }
    },
    [queryClient, refreshUser, t]
  );

  const handleCertificateUploadSuccess = useCallback(async (): Promise<void> => {
    await loadCertStatus();
    setShowCertForm(false);
    toast.success(t("settings.certificate.configured"));
  }, [loadCertStatus, t]);

  const handleRequestDeletion = useCallback(async (): Promise<void> => {
    setDeletionLoading(true);
    try {
      await requestTenantDeletion();
      await queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
      toast.success(t("settings.deletion.success"));
      setDeletionDialogOpen(false);
    } catch {
      toast.error(t("settings.deletion.error"));
    } finally {
      setDeletionLoading(false);
    }
  }, [queryClient, t]);

  const [bankTransferOpen, setBankTransferOpen] = useState(false);
  const [submittingTransfer, setSubmittingTransfer] = useState(false);
  /**
   * The checkout the payer is wiring against, opened when they expand the
   * panel. It exists before the money moves so the panel can show them the
   * reference derived from its id — without it they wire with nothing tying
   * the transfer to a row, and anything they type in their bank's concepto
   * field reads to the receipt verifier as someone else's reference.
   */
  const [transferCheckoutId, setTransferCheckoutId] = useState<string | null>(
    null,
  );

  /**
   * Pay by bank transfer, driven by the tenant themselves.
   *
   * This surface did not exist until spec a9b1ccad's tenant half was wired:
   * BankTransferPanel was only ever rendered on accountant pages, so a
   * business owner doing their own onboarding had no way to pay by wire at
   * all — they were told to "subí el comprobante en Facturación" and found
   * nothing there.
   */
  const handleBankTransfer = useCallback(
    async (receipt: File, shownCheckoutId: string | null): Promise<void> => {
      const selectedPlan = plans.find((p) => p.id === selectedPlanId);
      if (!selectedPlanId || !selectedPlan || submittingTransfer) return;
      setSubmittingTransfer(true);
      try {
        const res = await createBankTransferCheckout({
          planId: selectedPlanId,
          billingInterval: selectedBillingInterval,
          receipt,
          // Settle the row whose reference the panel showed. Omitted only if
          // the intent call failed, where a fresh row is still better than no
          // payment.
          settleCheckoutId: shownCheckoutId ?? undefined,
        });
        setBankTransferOpen(false);
        await queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
        // A monthly transfer whose receipt does not verify is HELD: paid, but
        // the plan is NOT granted until a human approves it. Claiming it is
        // active here is a lie the tenant discovers when they try to emit.
        toast.success(
          res?.activated !== false
            ? t("settings.plans.bankTransferActivated", "¡Pago registrado! Tu plan quedó activo.")
            : t(
                "settings.plans.bankTransferHeld",
                "Recibimos el comprobante. Lo estamos revisando y te avisamos apenas quede activo.",
              ),
        );
      } catch {
        toast.error(t("settings.plans.updateError"));
      } finally {
        setSubmittingTransfer(false);
      }
    },
    [
      selectedPlanId,
      selectedBillingInterval,
      plans,
      submittingTransfer,
      queryClient,
      t,
    ],
  );

  /**
   * Open (or adopt) the checkout as soon as the panel is expanded, and again
   * if the plan or cycle changes while it is open — the amount and therefore
   * the row differ per plan, and a reference for the wrong row is worse than
   * none. The backend reuses a matching pending checkout, so toggling the
   * accordion does not churn rows.
   */
  useEffect(() => {
    if (!bankTransferOpen || !selectedPlanId) return;
    let cancelled = false;
    void createBankTransferIntent({
      planId: selectedPlanId,
      billingInterval: selectedBillingInterval,
      returnContext: "settings",
    })
      .then((res) => {
        if (!cancelled) setTransferCheckoutId(res.checkoutId);
      })
      .catch(() => {
        // No reference rather than no transfer: the panel renders without the
        // row and submit falls back to creating the checkout itself.
        if (!cancelled) setTransferCheckoutId(null);
      });
    return () => {
      cancelled = true;
    };
  }, [bankTransferOpen, selectedPlanId, selectedBillingInterval]);

  const selectedTransferPlan = plans.find((p) => p.id === selectedPlanId);
  // What this tenant is actually charged, IVA included — the exact figure the
  // receipt verifier will match against. Never the catalog price.
  const transferAmountGross = selectedTransferPlan
    ? grossWithIva(tenantPlanPrice(selectedTransferPlan, selectedBillingInterval))
    : 0;

  const handleUpdatePlan = useCallback(async (): Promise<void> => {
    const selectedPlan = plans.find((p) => p.id === selectedPlanId);
    if (!selectedPlanId || !selectedPlan) return;
    setPlanSaving(true);
    try {
      // Which path this plan takes is a PRICE question, answered against the
      // tenant-resolved price — never `isFreePlan()`, which is the premium-
      // FEATURE gate and lists the billable `semilla` ($99) and `tala` ($513)
      // tiers. Routing on it sent both down the activation path, where the
      // backend guard answers 400 "Paid plans require completing checkout".
      // Onboarding hit exactly this and fixed it; settings was the site left
      // behind, so buying Semilla or Tala from here was impossible.
      if (planUsesActivationPath(selectedPlanId, selectedBillingInterval, plans)) {
        const res = await updateTenantPlan(
          selectedPlanId,
          selectedBillingInterval,
          "settings"
        );
        await queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
        if (res.redirectUrl) {
          window.location.href = res.redirectUrl;
          return;
        }
        toast.success(t("settings.plans.updateSuccess"));
        return;
      }
      // A card checkout cancels every pending row, the open transfer intent
      // included. Drop it rather than keep showing a reference for a row that
      // no longer exists.
      setTransferCheckoutId(null);
      const res = await createPlanCheckout({
        planId: selectedPlanId,
        billingInterval: selectedBillingInterval,
        returnContext: "settings",
      });
      await queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
      if (res.redirectUrl) {
        window.location.href = res.redirectUrl;
        return;
      }
      toast.success(t("settings.plans.updateSuccess"));
    } catch (err) {
      // Both calls above refuse with an explanation the tenant can act on —
      // a missing Decreto 206/019 declaration, a plan their régimen cannot
      // hold. Showing the generic "Intentá de nuevo" for those tells them to
      // retry something retrying cannot fix, and Nest never logs a thrown
      // HttpException, so discarding it here erases the reason everywhere.
      toast.error(serverMessage(err) ?? t("settings.plans.updateError"));
    } finally {
      setPlanSaving(false);
    }
  }, [selectedPlanId, selectedBillingInterval, plans, queryClient, t]);

  const handleSavePublicName = useCallback(async (): Promise<void> => {
    const trimmed = publicNameValue.trim();
    if (trimmed === (tenant?.name ?? "")) return;
    setPublicNameSaving(true);
    try {
      await updateTenant({ name: trimmed || undefined });
      await queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
      toast.success(t("settings.publicName.success"));
    } catch {
      toast.error(t("tenants.edit.errorUpdate"));
    } finally {
      setPublicNameSaving(false);
    }
  }, [publicNameValue, tenant?.name, queryClient, t]);

  const handleSaveBrandColor = useCallback(
    async (brandColor: string | null): Promise<void> => {
      setBrandColorSaving(true);
      try {
        await updateTenantBranding(brandColor);
        await queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
        toast.success(t("settings.brandColor.success"));
      } catch {
        toast.error(t("settings.brandColor.error"));
      } finally {
        setBrandColorSaving(false);
      }
    },
    [queryClient, t]
  );

  const handleSaveAdenda = useCallback(
    async (adendaText: string): Promise<void> => {
      setAdendaSaving(true);
      try {
        // Empty saves as "no standing adenda" — the backend stores NULL, which
        // is what the marca-inherits-company rule reads.
        await updateTenantAdenda(adendaText.length > 0 ? adendaText : null);
        await queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
        toast.success(t("adenda.saved", "Adenda guardada."));
      } catch {
        toast.error(t("adenda.saveError", "No pudimos guardar la adenda."));
      } finally {
        setAdendaSaving(false);
      }
    },
    [queryClient, t]
  );

  const handleSaveInvoiceLogoCrop = useCallback(
    async (payload: InvoiceLogoCropPayload): Promise<void> => {
      setInvoiceLogoCropSaving(true);
      try {
        // Pass the CURRENT colour through: the endpoint owns both, and sending
        // brandColor: null would clear a colour the user never touched.
        await updateTenantBranding(tenant?.brandColor ?? null, payload);
        await queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
        toast.success(t("settings.companyImage.crop.success"));
      } catch {
        toast.error(t("settings.companyImage.crop.error"));
      } finally {
        setInvoiceLogoCropSaving(false);
      }
    },
    [queryClient, t, tenant?.brandColor]
  );

  /**
   * Upload a newly picked logo and apply the crop drawn for it, as one action.
   *
   * Order matters: the upload RESETS the stored crop (a crop is only meaningful
   * against the image it was drawn on), so the crop must be saved after it, not
   * before, or it would be wiped by its own upload.
   */
  const handleUploadInvoiceLogoWithCrop = useCallback(
    async (
      file: File,
      target: "both" | "invoice",
      crop: InvoiceLogoCropPayload
    ): Promise<void> => {
      const setUploading = target === "both" ? setCompanyImageUploading : setInvoiceLogoUploading;
      setUploading(true);
      try {
        await uploadCurrentTenantImage(file, target);
        await updateTenantBranding(tenant?.brandColor ?? null, crop);
        await queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
        await refreshUser();
        toast.success(t("settings.companyImage.success"));
      } catch {
        toast.error(t("settings.companyImage.error"));
      } finally {
        setUploading(false);
      }
    },
    [queryClient, refreshUser, t, tenant?.brandColor]
  );

  /**
   * The "different image on invoices" toggle used to be local state only, so
   * unticking it re-rendered the UI but left the separately-uploaded invoice
   * logo on the record — the PDF kept using the old image with no way back.
   * Turning sharing ON now clears it server-side.
   *
   * Turning it OFF needs no call: the user is about to pick a dedicated image,
   * and that upload is what sets invoiceLogoImage.
   */
  const handleUseSameLogoChange = useCallback(
    (nextSame: boolean): void => {
      // Turning sharing OFF is purely local: the user is about to pick a
      // dedicated image, and that upload is what sets invoiceLogoImage.
      if (!nextSame) {
        setUseSameLogo(false);
        return;
      }
      if (tenant?.invoiceLogoImage == null) {
        setUseSameLogo(true); // already shared; nothing to persist
        return;
      }
      // Turning it ON is deliberately NOT optimistic. The checkbox is derived
      // from `invoiceLogoImage == null`, so letting the refetch drive it means
      // the box only moves once the server actually cleared the field. Setting
      // it eagerly made the UI claim the logos were shared while the invoice
      // still rendered the old dedicated image — a state that looks saved and
      // is not.
      void (async () => {
        setInvoiceLogoUploading(true);
        try {
          await apiUseSiteLogoForInvoice(tenant?.brandColor ?? null);
          await queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
          await refreshUser();
          // Explicit confirmation: this toggle changes what every future
          // invoice renders, and silence is indistinguishable from a no-op.
          toast.success(t("settings.companyImage.nowSharedWithSite"));
        } catch {
          toast.error(t("settings.companyImage.error"));
        } finally {
          setInvoiceLogoUploading(false);
        }
      })();
    },
    [queryClient, refreshUser, t, tenant?.brandColor, tenant?.invoiceLogoImage]
  );

  const handlePreviewInvoice = useCallback(async (): Promise<void> => {
    setPreviewPdfLoading(true);
    try {
      const { data } = await apiClient.get<Blob>("/tenants/current/invoice-preview-pdf", {
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch {
      toast.error(t("settings.previewInvoice.error"));
    } finally {
      setPreviewPdfLoading(false);
    }
  }, [t]);

  /**
   * The legal entity as the first row of Marcas. Its name is the razón social,
   * which is not editable here — the row edits the IDENTITY (logo, crop,
   * colour), not the company's legal details.
   */
  const [invoiceIdentityOpen, setInvoiceIdentityOpen] = useState(false);
  const [fiscalIdentityOpen, setFiscalIdentityOpen] = useState(false);
  /** The legal entity as a row of the Marcas list. Built once: the card is
   *  rendered twice (plain and behind the plan gate) and the two had already
   *  started to drift. */
  const legalEntityRow = tenant
    ? {
        name: tenant.legalName?.trim() || tenant.name,
        onEdit: () => setInvoiceIdentityOpen(true),
        // A dedicated invoice logo is exactly "invoiceLogoImage is set";
        // null means it shares the company one.
        hasOwnLogo: tenant.invoiceLogoImage != null,
        onUseCompanyLogo: () => void handleUseSameLogoChange(true),
        brandColor: tenant.brandColor,
        onSaveColor: (color: string | null) => void handleSaveBrandColor(color),
        colorSaving: brandColorSaving,
      }
    : undefined;
  const legalEntityIdentity = useMemo(
    () =>
      tenant
        ? {
            name: tenant.legalName?.trim() || tenant.name,
            section: {
              tenant,
              useSameLogo,
              onUseSameLogoChange: handleUseSameLogoChange,
              bothUploading: companyImageUploading,
              invoiceUploading: invoiceLogoUploading,
              onInvoiceLogoChange: handleInvoiceLogoChange,
              onSaveBrandColor: handleSaveBrandColor,
              brandColorSaving,
              onPreviewInvoice: handlePreviewInvoice,
              previewPdfLoading,
              onSaveInvoiceLogoCrop: handleSaveInvoiceLogoCrop,
              invoiceLogoCropSaving,
              onUploadInvoiceLogoWithCrop: handleUploadInvoiceLogoWithCrop,
            },
          }
        : undefined,
    [
      tenant,
      useSameLogo,
      handleUseSameLogoChange,
      companyImageUploading,
      invoiceLogoUploading,
      handleInvoiceLogoChange,
      handleSaveBrandColor,
      brandColorSaving,
      handlePreviewInvoice,
      previewPdfLoading,
      handleSaveInvoiceLogoCrop,
      invoiceLogoCropSaving,
      handleUploadInvoiceLogoWithCrop,
    ]
  );

  if (!user?.tenantId) {
    return (
      <div className="space-y-4 fx-content">
        <PageHeader
          eyebrow={t("settings.eyebrow", "Tu empresa")}
          title={t("settings.title")}
          description={t("settings.description")}
        />
      </div>
    );
  }

  if (tenantLoading || !tenant) {
    return <LoadingPage />;
  }

  if (tabParam === "documents") {
    return <Navigate to="/documents" replace />;
  }

  const addressParts = [tenant.address, tenant.city, tenant.department].filter(Boolean);
  const addressLine = addressParts.length > 0 ? addressParts.join(", ") : null;
  const deletionRequested = tenant.deletionRequestedAt != null;

  return (
    <div className="space-y-4 fx-content">
      <PageHeader
        eyebrow={t("settings.eyebrow", "Tu empresa")}
        title={t("settings.title")}
        description={t("settings.description")}
        actions={
          !deletionRequested ? (
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={() => setDeletionDialogOpen(true)}
              className="transition-colors duration-150"
            >
              {t("settings.deletion.button")}
            </Button>
          ) : undefined
        }
      />

      {deletionRequested && (
        <Alert variant="warning" className="transition-colors duration-150">
          <AlertCircle className="size-4 shrink-0" aria-hidden />
          <AlertDescription>{t("settings.deletion.alreadyRequested")}</AlertDescription>
        </Alert>
      )}

      <Tabs
        value={effectiveTab ?? TAB_COMPANY}
        onValueChange={setTab}
        className="w-full"
      >
        <TabsList className="mb-4 gap-2">
          <TabsTrigger value={TAB_COMPANY}>{t("settings.tabs.company")}</TabsTrigger>
          {!tenantIsFree && (
            <TabsTrigger value={TAB_FISCAL_LEGAL}>{t("settings.tabs.fiscalLegal")}</TabsTrigger>
          )}
          <TabsTrigger value={TAB_CERTIFICATES}>{t("settings.tabs.facturaElectronica")}</TabsTrigger>
          {!isAccountant && (
            <TabsTrigger value={TAB_BILLING}>{t("settings.tabs.billing")}</TabsTrigger>
          )}
          {!tenantIsFree && !isAccountant && (
            <TabsTrigger value={TAB_API}>{t("settings.tabs.apiIntegration")}</TabsTrigger>
          )}
          {!tenantIsFree && !isAccountant && (
            <TabsTrigger value={TAB_INTEGRACIONES}>{t("settings.tabs.integraciones")}</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value={TAB_COMPANY} className="mt-0 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-foreground">
                {t("settings.publicName.title")}
              </CardTitle>
              <CardDescription>
                {t("settings.publicName.description")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="settings-public-name">
                  {t("settings.publicName.label")}
                </Label>
                <Input
                  id="settings-public-name"
                  value={publicNameValue}
                  onChange={(e) => setPublicNameValue(e.target.value)}
                  placeholder={t("settings.publicName.placeholder")}
                  disabled={publicNameSaving}
                  className="max-w-md transition-colors duration-150"
                />
              </div>
              <div className="flex justify-end">
                <Button
                  type="button"
                  onClick={handleSavePublicName}
                  disabled={
                    publicNameSaving ||
                    publicNameValue.trim() === (tenant?.name ?? "")
                  }
                  className="transition-colors duration-150"
                >
                  {publicNameSaving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t("settings.publicName.saving")}
                    </>
                  ) : (
                    t("settings.publicName.save")
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
          {/* ONE card. This used to be two: the read-only legal data, and
              directly beneath it "Datos fiscales del emisor" re-rendering the
              same RUT, razón social, dirección, ciudad, departamento, teléfono
              and correo as inputs. Seven values twice on one page, which read
              as two different records rather than one record and its editor.
              The editable half now lives behind Editar. */}
          <SettingsLegalInfoCard
            tenant={tenant}
            addressLine={addressLine}
            onEdit={() => setFiscalIdentityOpen(true)}
          />
          <Dialog
            open={fiscalIdentityOpen}
            onOpenChange={setFiscalIdentityOpen}
          >
            <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
              <DialogHeader>
                <DialogTitle>
                  {t("settings.legalInfo.editTitle", "Datos del emisor")}
                </DialogTitle>
                <DialogDescription>
                  {t(
                    "settings.legalInfo.editHelp",
                    "El RUT y la razón social los define DGI y no se editan acá.",
                  )}
                </DialogDescription>
              </DialogHeader>
              {/* variant="plain": the dialog is already a frame, and the
                  accountant's client-detail page still renders this as a
                  card. The sucursal/domicilio warning renders in both. */}
              <SettingsFiscalIdentityCard
                variant="plain"
                scopeKey="current"
                load={getFiscalIdentity}
                loadFromDgi={getFiscalIdentityFromDgi}
                save={updateFiscalIdentity}
              />
            </DialogContent>
          </Dialog>
          {/* Portal logo only. The invoice half moved into Marcas below —
              it is the identity a client is billed under, which is what a
              marca is, so the two belong in one list. */}
          <CompanyImageCard
            tenant={tenant}
            showInvoiceSection={false}
            useSameLogo={useSameLogo}
            onUseSameLogoChange={handleUseSameLogoChange}
            bothUploading={companyImageUploading}
            siteUploading={siteLogoUploading}
            invoiceUploading={invoiceLogoUploading}
            onBothLogoChange={handleBothLogoChange}
            onSiteLogoChange={handleSiteLogoChange}
            onInvoiceLogoChange={handleInvoiceLogoChange}
            onSaveBrandColor={handleSaveBrandColor}
            brandColorSaving={brandColorSaving}
            onEditInvoiceIdentity={() => setInvoiceIdentityOpen(true)}
          />
          {/* Its own card, between the logo and Marcas. It shipped inside the
              invoice-identity dialog first and users could not find it: a
              card titled "Logo de la empresa" does not read as the home of a
              fiscal legend. Here the reading order is logo, then the text
              every invoice carries, then the marcas that can override it. */}
          <CompanyAdendaCard
            savedAdenda={tenant.adendaText}
            saving={adendaSaving}
            onSave={handleSaveAdenda}
          />
          {/* Below the company logo card on purpose: that card is the LEGAL
              entity's branding, which every brand falls back to.

              Marcas are Ombú+. A lower tier sees the card blurred behind the
              usual upgrade overlay rather than not at all — the same treatment
              PaidPlanRoute gives premium routes, so the feature is
              discoverable instead of invisible. The backend returns an empty
              brand list for these tenants, so every OTHER surface (invoice
              picker, client default, API-key column) disappears on its own. */}
          {isProOrUltraPlan(tenant.planId) ? (
            <BrandsCard
              tenantInvoiceLogoUrl={tenant.invoiceLogoImage ?? tenant.profileImage}
              companyAdendaText={tenant.adendaText}
              legalEntity={legalEntityRow}
            />
          ) : (
            <PlanGateOverlay variant="paid">
              <BrandsCard
                tenantInvoiceLogoUrl={tenant.invoiceLogoImage ?? tenant.profileImage}
                companyAdendaText={tenant.adendaText}
                legalEntity={legalEntityRow}
              />
            </PlanGateOverlay>
          )}
          {/* ONE invoice-identity editor, reachable from the company-logo
              card and from the legal-entity row in Marcas. Owned here rather
              than by either card so the two entry points cannot drift into
              two dialogs. */}
          {legalEntityIdentity && (
            <Dialog
              open={invoiceIdentityOpen}
              onOpenChange={setInvoiceIdentityOpen}
            >
              {/* Wider than the marca dialog: it hosts the logo tile, the crop
                  control and the colour panel with its suggestions. */}
              <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{legalEntityIdentity.name}</DialogTitle>
                  <DialogDescription>
                    {t(
                      "brands.legalEntityDialogHelp",
                      "Logo y color de la factura cuando no lleva marca. El nombre es la razón social de la empresa y no se edita acá."
                    )}
                  </DialogDescription>
                </DialogHeader>
                {/* Each control inside saves on its own — no footer to
                    confirm, matching how this section behaved as a card. */}
                <CompanyInvoiceIdentitySection {...legalEntityIdentity.section} />
              </DialogContent>
            </Dialog>
          )}
        </TabsContent>

        {!tenantIsFree && (
          <TabsContent value={TAB_FISCAL_LEGAL} className="mt-0 space-y-4">
            <SettingsFiscalProfileCard tenant={tenant} />
            <SettingsFiscalAutomationCard tenant={tenant} />
            <SettingsAutoAcceptReceivedCfeCard tenant={tenant} />
            <SettingsAutoAcceptRucsCard />
          </TabsContent>
        )}

        <TabsContent value={TAB_CERTIFICATES} className="mt-0 space-y-4">
          <Card>
        <CardHeader>
          <CardTitle className="text-base text-foreground">{t("settings.certificate.title")}</CardTitle>
          <CardDescription>{t("settings.certificate.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          {certLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>{t("settings.certificate.loading")}</span>
            </div>
          ) : certStatus?.hasCredentials && !showCertForm ? (
            <div className="space-y-3">
              <StatusBadge variant="success">
                {t("settings.certificate.configured")}
              </StatusBadge>
              {certStatus.certificateValidFrom && certStatus.certificateValidTo && (
                <CertificateValidityBar
                  validFrom={certStatus.certificateValidFrom}
                  validTo={certStatus.certificateValidTo}
                  ariaLabel={t("settings.certificate.validFromTo", {
                    from: certStatus.certificateValidFrom,
                    to: certStatus.certificateValidTo,
                  })}
                />
              )}
              <div className="grid gap-2">
                <Label>{t("tenants.edit.dgi.passphraseLabel")}</Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 rounded-md bg-muted px-3 py-2 text-sm font-mono">
                    {revealedPassphrase === null
                      ? "••••••••"
                      : revealedPassphrase === ""
                        ? t("tenants.edit.dgi.noPassphrase", { lng: "es" })
                        : revealedPassphrase}
                  </code>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => void handleTogglePassphrase()}
                    disabled={passphraseRevealing}
                  >
                    {passphraseRevealing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : revealedPassphrase !== null ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                    <span className="ml-1.5">
                      {revealedPassphrase !== null
                        ? t("tenants.edit.dgi.hide", { lng: "es" })
                        : t("tenants.edit.dgi.reveal", { lng: "es" })}
                    </span>
                  </Button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void handleCertDownload()}
                  disabled={certDownloading}
                  className="transition-colors duration-150"
                >
                  {certDownloading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  <span className="ml-1.5">{t("tenants.edit.dgi.download", { lng: "es" })}</span>
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCertForm(true)}
                  className="transition-colors duration-150"
                >
                  {t("settings.certificate.replaceCertificate")}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {!certStatus?.hasCredentials && !showCertForm && (
                <div className="space-y-2">
                  <StatusBadge variant="danger">
                    {t("settings.certificate.noCertificateBadge")}
                  </StatusBadge>
                  <p className="text-sm text-muted-foreground">
                    {t("settings.certificate.noCertificateHint")}
                  </p>
                </div>
              )}
              <CertificateUploadForm
                tenantRut={tenant?.rut ?? null}
                onSuccess={handleCertificateUploadSuccess}
                submitButtonLabel={
                  certStatus?.hasCredentials
                    ? t("tenants.edit.dgi.replace")
                    : t("settings.certificate.uploadCertificate")
                }
                showCancel={showCertForm}
                onCancel={() => setShowCertForm(false)}
                idPrefix="settings-cert"
              />
            </div>
          )}
          </CardContent>
          </Card>

          <SettingsCaeCard />

          <Card>
            <CardHeader>
              <CardTitle className="text-base text-foreground">{t("settings.cfe.title")}</CardTitle>
              <CardDescription>{t("settings.cfe.description")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <p className="text-sm font-medium text-foreground">
                  {t("settings.cfe.importGroupLabel", "Importar")}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t("settings.cfe.importHint", "Subí archivos XML o ZIP desde DGI u otro sistema.")}
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setImportEmittedOpen(true)}
                    className="transition-colors duration-150 hover:bg-muted/80 hover:text-foreground"
                  >
                    <Upload className="mr-2 h-4 w-4 shrink-0" />
                    {t("settings.cfe.importEmitted")}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setImportRecibidasOpen(true)}
                    className="transition-colors duration-150 hover:bg-muted/80 hover:text-foreground"
                  >
                    <Upload className="mr-2 h-4 w-4 shrink-0" />
                    {t("settings.cfe.importRecibidas")}
                  </Button>
                </div>
              </div>
              <div className="border-t pt-4">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-foreground">
                    {t("settings.cfe.exportGroupLabel", "Exportar")}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t("settings.cfe.exportHint", "Se descarga un ZIP con los CFE (emitidos aceptados o recibidos importados).")}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      disabled={exportEmittedLoading}
                      className="transition-colors duration-150 hover:bg-muted/80 hover:text-foreground"
                      onClick={async () => {
                        setExportEmittedLoading(true);
                        try {
                          await downloadExportEmittedCfes();
                          toast.success(t("settings.cfe.exportEmittedSuccess", "CFE emitidos descargados."));
                        } catch {
                          toast.error(t("settings.cfe.exportEmittedError", "No se pudo descargar el archivo."));
                        } finally {
                          setExportEmittedLoading(false);
                        }
                      }}
                    >
                      {exportEmittedLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 shrink-0 animate-spin" />
                          {t("settings.cfe.exportEmitted")}
                        </>
                      ) : (
                        <>
                          <Download className="mr-2 h-4 w-4 shrink-0" />
                          {t("settings.cfe.exportEmitted")}
                        </>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={exportRecibidasLoading}
                      className="transition-colors duration-150 hover:bg-muted/80 hover:text-foreground"
                      onClick={async () => {
                        setExportRecibidasLoading(true);
                        try {
                          await downloadExportRecibidasCfes();
                          toast.success(t("settings.cfe.exportRecibidasSuccess", "CFE recibidos descargados."));
                        } catch {
                          toast.error(t("settings.cfe.exportRecibidasError", "No se pudo descargar el archivo."));
                        } finally {
                          setExportRecibidasLoading(false);
                        }
                      }}
                    >
                      {exportRecibidasLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 shrink-0 animate-spin" />
                          {t("settings.cfe.exportRecibidas")}
                        </>
                      ) : (
                        <>
                          <Download className="mr-2 h-4 w-4 shrink-0" />
                          {t("settings.cfe.exportRecibidas")}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <ImportEmittedCfeDialog
            tenantId={user?.tenantId ?? ""}
            tenantName={tenant?.name ?? ""}
            open={importEmittedOpen}
            onOpenChange={setImportEmittedOpen}
            importFn={importEmittedCfes}
          />
          <ImportRecibidasCfeDialog
            tenantId={user?.tenantId ?? ""}
            tenantName={tenant?.name ?? ""}
            open={importRecibidasOpen}
            onOpenChange={setImportRecibidasOpen}
            importFn={importRecibidasCfes}
          />
        </TabsContent>

        {!isAccountant && (
          <TabsContent value={TAB_BILLING} className="mt-0 space-y-4">
            <SettingsPlanCard
              tenant={tenant}
              plans={plans}
              partnerBenefit={partnerBenefit}
              selectedPlanId={selectedPlanId}
              selectedBillingInterval={selectedBillingInterval}
              onPlanChange={setSelectedPlanId}
              onBillingIntervalChange={setSelectedBillingInterval}
              onUpdatePlan={handleUpdatePlan}
              saving={planSaving}
              onOpenLiteralEInfo={() => setLiteralEInfoModalOpen(true)}
            />
            {/* Pay by wire instead of card. Only for a plan that actually
                costs something: a free or fully-discounted plan has no payment
                to prove, and the backend refuses a zero-price transfer. */}
            {selectedTransferPlan && transferAmountGross > 0 ? (
              <BankTransferPanel
                open={bankTransferOpen}
                onToggle={() => setBankTransferOpen((o) => !o)}
                onSubmit={handleBankTransfer}
                submitting={submittingTransfer}
                amountGrossPesos={transferAmountGross}
                checkoutId={transferCheckoutId}
                billingInterval={selectedBillingInterval}
              />
            ) : null}
            <SubscriptionInvoicesTable />
          </TabsContent>
        )}

        {!tenantIsFree && !isAccountant && (
          <TabsContent value={TAB_API} className="mt-0 space-y-4">
            <SettingsApiIntegrationCard planId={tenant?.planId} />
          </TabsContent>
        )}

        {!tenantIsFree && !isAccountant && (
          <TabsContent value={TAB_INTEGRACIONES} className="mt-0 space-y-4">
            <SettingsDeelIntegrationCard />
            <SettingsMercadopagoIntegrationCard />
          </TabsContent>
        )}
      </Tabs>

      <LiteralEInfoModal
        open={literalEInfoModalOpen}
        onOpenChange={setLiteralEInfoModalOpen}
      />
      <ConfirmationDialog
        open={deletionDialogOpen}
        onOpenChange={setDeletionDialogOpen}
        title={t("settings.deletion.dialogTitle")}
        description={t("settings.deletion.dialogDescription")}
        confirmText={t("settings.deletion.confirm")}
        cancelText={t("settings.deletion.cancel")}
        loadingText={t("settings.deletion.submitting")}
        variant="destructive"
        isLoading={deletionLoading}
        onConfirm={handleRequestDeletion}
      />
    </div>
  );
}

